/* ds-preorder.js (v2)
   Pre-order handling for Displaysense product pages.

   Watches the theme's own product form for the selected variant (the hidden or select input name="id"),
   then for that variant:
     - shows the Pre-order pill ("Order now, despatch on or before 16th Oct 2026") and a reassurance row
     - relabels the theme's add-to-basket button to "Pre-order now" (or "Order now" for lead time)
     - adds a visible line item property "Despatch" so the basket, checkout and order show the date
     - hides the Buy it now / Shop Pay button so pre-orders always pass through the basket
     - shows "Need it sooner? These are in stock now" with up to three in-stock alternatives
     - keeps the notify-me form pointed at the right variant
     - quick "Add" on each alternative card (AJAX, updates the header basket count and mini basket)
     - card prices follow the theme's inc / ex VAT switch
     - badges the width and colour buttons "Pre-order" before anyone clicks them
     - pushes GA4 events to the dataLayer (GTM): dspo_preorder_view, dspo_alt_click, dspo_alt_add,
       dspo_preorder_add, dspo_notify_submit
   Availability comes from the custom.availability variant metafield, never from inventory.
*/
(function () {
  'use strict';

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var PROP_NAME = 'properties[Despatch]';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- despatch countdown (Mon to Fri, Europe/London) ---------- */

  function londonNow() {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', hour12: false, weekday: 'short',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).formatToParts(new Date()).reduce(function (o, p) { o[p.type] = p.value; return o; }, {});
    return {
      day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday),
      secs: (parseInt(parts.hour, 10) % 24) * 3600 + parseInt(parts.minute, 10) * 60 + parseInt(parts.second, 10)
    };
  }

  function nextWorkday(d) { d = (d + 1) % 7; while (d === 0 || d === 6) d = (d + 1) % 7; return d; }

  function countdownHtml(cutoffHour) {
    // Bank holidays are not handled; add a date list here if you want them respected.
    var now = londonNow();
    var cutoff = cutoffHour * 3600;
    var workday = now.day >= 1 && now.day <= 5;
    if (workday && now.secs < cutoff) {
      var left = cutoff - now.secs;
      var h = Math.floor(left / 3600), m = Math.floor((left % 3600) / 60);
      return 'Order in the next <strong>' + (h ? h + 'h ' : '') + m + 'm</strong> for despatch ' + DAYS[nextWorkday(now.day)] + '.';
    }
    var orderDay = workday ? nextWorkday(now.day) : nextWorkday(now.day === 6 ? 5 : now.day === 0 ? 5 : now.day);
    return 'Order before ' + cutoffHour + ':00 on ' + DAYS[orderDay] + ' for despatch ' + DAYS[nextWorkday(orderDay)] + '.';
  }

  /* ---------- finding the theme's form and button ---------- */

  function findForm(root) {
    var id = root.getAttribute('data-form-id');
    var f = id ? document.getElementById(id) : null;
    if (f && f.tagName !== 'FORM') f = f.querySelector('form') || null;
    if (f) return f;
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      // skip quick-add forms inside product cards / recommendations
      if (forms[i].closest('.card, .product-card, .card-wrapper, product-recommendations, .product-recommendations, .quick-add-modal')) continue;
      if (forms[i].querySelector('[name="id"]')) return forms[i];
    }
    return null;
  }

  function findButton(root, form) {
    var sel = root.getAttribute('data-button-selector');
    var scope = form;
    var btn = sel ? (form.querySelector(sel) || document.querySelector(sel)) : null;
    if (!btn) btn = scope.querySelector('button[name="add"], [type="submit"][name="add"]');
    if (!btn && form.id) btn = document.querySelector('button[form="' + form.id + '"][type="submit"]');
    if (!btn) btn = scope.querySelector('button[type="submit"], input[type="submit"]');
    return btn;
  }

  function buttonLabelNode(btn) {
    if (!btn) return null;
    if (btn.tagName === 'INPUT') return btn;
    // prefer an inner text span (most themes wrap the label so a spinner can sit beside it)
    var spans = btn.querySelectorAll('span');
    for (var i = 0; i < spans.length; i++) {
      if (spans[i].children.length === 0 && spans[i].textContent.trim().length > 2) return spans[i];
    }
    return btn;
  }

  /* ---------- despatch date ("on or before") ---------- */

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function todayIso() {
    var p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date()).reduce(function (o, x) { o[x.type] = x.value; return o; }, {});
    return p.year + '-' + p.month + '-' + p.day;
  }

  // Returns { text, isDate } for a variant: a future eta_date wins ("16 Oct 2026"), else the free-text ETA.
  // A date that has already passed is ignored so the page never promises a date in the past.
  function etaFor(v) {
    if (v && v.eta_date && v.eta_date >= todayIso()) {
      var d = v.eta_date.split('-');
      var day = parseInt(d[2], 10);
      var sfx = (day % 100 >= 11 && day % 100 <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
      return { text: day + sfx + ' ' + MONTHS[parseInt(d[1], 10) - 1] + ' ' + d[0], isDate: true };
    }
    return { text: (v && v.eta) || '', isDate: false };
  }

  /* ---------- analytics (GA4 via GTM dataLayer) ---------- */

  function track(event, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
      window.dataLayer.push(payload);
    } catch (e) {}
  }

  /* ---------- inc / ex VAT: follow whatever the theme's main price is showing ---------- */

  function vatMode() {
    var ex = document.querySelector('.cc-price-amount.js-ex-vat, .js-product-price.js-ex-vat');
    if (ex && ex.offsetParent !== null && getComputedStyle(ex).display !== 'none') return 'ex';
    var off = document.getElementById('toggle-vat-off');
    if (off && off.checked) return 'ex';
    return 'inc';
  }

  /* ---------- basket (quick add) ---------- */

  function refreshBasket() {
    // Header count
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
      document.querySelectorAll('[data-cart-item-count], .js-cart-count').forEach(function (el) { el.textContent = cart.item_count; });
    }).catch(function () {});
    // Mini basket contents (best effort; the theme rebuilds it from the cc-header section)
    fetch('/?sections=cc-header', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (data) {
      var html = data && data['cc-header'];
      if (!html) return;
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      [['[data-cart-contents]'], ['.mini-cart-summary']].forEach(function (s) {
        var fresh = tmp.querySelector(s[0]), live = document.querySelector(s[0]);
        if (fresh && live) { live.innerHTML = fresh.innerHTML; live.className = fresh.className; }
      });
      var empty = document.querySelector('[data-cart-empty]');
      if (empty) empty.classList.add('hidden');
    }).catch(function () {});
    ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (n) {
      try { document.dispatchEvent(new CustomEvent(n, { bubbles: true })); } catch (e) {}
    });
  }

  function openBasket() {
    var btn = document.querySelector('.btn-cart');
    if (btn) { btn.click(); return; }
    window.location.href = '/cart';
  }

  /* ---------- despatch note: make sure every pre-order line in the basket carries it ---------- */

  function despatchText(v) {
    if (!v) return '';
    var st = (v.state || '').toLowerCase();
    var e = etaFor(v);
    if (st === 'preorder') return e.isDate ? 'Pre-order, despatch on or before ' + e.text : 'Pre-order, despatches ' + (e.text || 'when stock lands');
    if (st === 'lead_time') return e.isDate ? 'Made to order, despatch on or before ' + e.text : 'Made to order, lead time ' + (e.text || 'to be confirmed');
    return '';
  }

  // variant id -> despatch text, for every flagged variant on this page
  var KNOWN = {};

  // Some add-to-basket scripts send only the variant id and quantity, which drops the form's
  // properties. After any add, check the basket and put the Despatch note back on flagged lines.
  var ensuring = false;
  function ensureDespatch() {
    if (ensuring || !Object.keys(KNOWN).length) return;
    ensuring = true;
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
      var fixes = (cart.items || []).filter(function (it) {
        var want = KNOWN[String(it.variant_id)];
        return want && !(it.properties && it.properties.Despatch);
      });
      return fixes.reduce(function (p, it) {
        return p.then(function () {
          var props = {};
          for (var k in (it.properties || {})) if (Object.prototype.hasOwnProperty.call(it.properties, k)) props[k] = it.properties[k];
          props.Despatch = KNOWN[String(it.variant_id)];
          return fetch('/cart/change.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: props })
          });
        });
      }, Promise.resolve()).then(function () { if (fixes.length) refreshBasket(); });
    }).catch(function () {}).then(function () { ensuring = false; });
  }
  function isCartAdd(url) { return typeof url === 'string' && url.indexOf('/cart/add') !== -1; }
  (function hookCartAdds() {
    if (window.__dspoHooked) return;
    window.__dspoHooked = true;
    if (window.fetch) {
      var f = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var res = f.apply(this, arguments);
        if (isCartAdd(url)) res.then(function () { setTimeout(ensureDespatch, 250); }, function () {});
        return res;
      };
    }
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (isCartAdd(String(url))) this.addEventListener('load', function () { setTimeout(ensureDespatch, 250); });
      return open.apply(this, arguments);
    };
  })();

  /* ---------- width / colour buttons: badge the ones on pre-order ---------- */

  var STATE_TTL = 10 * 60 * 1000;
  var stateCache = {};

  function getState(handle) {
    if (stateCache[handle]) return stateCache[handle];
    try {
      var raw = sessionStorage.getItem('dspo:' + handle);
      if (raw) {
        var hit = JSON.parse(raw);
        if (Date.now() - hit.t < STATE_TTL) { stateCache[handle] = Promise.resolve(hit.v); return stateCache[handle]; }
      }
    } catch (e) {}
    stateCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '?view=dspo-state', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (t) {
        var v = null;
        try { v = t ? JSON.parse(t.trim()) : null; } catch (e) { v = null; }
        try { if (v) sessionStorage.setItem('dspo:' + handle, JSON.stringify({ t: Date.now(), v: v })); } catch (e) {}
        return v;
      })
      .catch(function () { return null; });
    return stateCache[handle];
  }

  // Which product a swatch leads to. Single-option swatches carry data-product-handle; multi-option
  // swatches (colour + width) leave it blank and the theme resolves it from #size-swatch-wrapper's
  // data-combinations map, keyed by the selected labels in data-json-option-order. Same logic here.
  function readJSON(el, attr) {
    var raw = el && el.getAttribute(attr);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }
  function cleanHandle(h) {
    if (!h) return '';
    return String(h).split('?')[0].split('/').pop().trim();
  }
  function swatchHandle(b) {
    var direct = cleanHandle(b.getAttribute('data-product-handle'));
    if (direct) return direct;
    var wrap = document.getElementById('size-swatch-wrapper');
    var combos = readJSON(wrap, 'data-combinations');
    if (!combos) return '';
    var sel = readJSON(wrap, 'data-current-selections') || {};
    var groups = document.querySelectorAll('.cc-swatch-option-group');
    groups.forEach(function (g) {
      var cur = g.querySelector('.btn-colour-swatch.current, .btn-size-swatch.current, .btn-image-swatch.current');
      var name = g.getAttribute('data-swatch-name');
      if (name && cur && cur.getAttribute('data-swatch-label')) sel[name] = cur.getAttribute('data-swatch-label');
    });
    var opt = b.getAttribute('data-option-name') || (b.closest('.cc-swatch-option-group') || { getAttribute: function () { return null; } }).getAttribute('data-swatch-name');
    if (opt) sel[opt] = b.getAttribute('data-swatch-label');
    var order = wrap.getAttribute('data-json-option-order');
    var names = order ? order.split('||') : Array.prototype.map.call(groups, function (g) { return g.getAttribute('data-swatch-name'); });
    var key = names.filter(function (n) { return n && sel[n]; }).map(function (n) { return sel[n]; }).join('|');
    return cleanHandle(combos[key]);
  }

  function decorateSwatches() {
    var btns = document.querySelectorAll('.btn-size-swatch, .btn-colour-swatch, .btn-image-swatch');
    btns.forEach(function (b) {
      var handle = swatchHandle(b);
      if (!handle || b.getAttribute('data-dspo-badged') === handle) return;
      b.setAttribute('data-dspo-badged', handle);
      getState(handle).then(function (s) {
        var isColour = b.classList.contains('btn-colour-swatch') || b.classList.contains('btn-image-swatch');
        // Colour buttons sit in a fixed-size frame; badge the frame so the dot isn't clipped
        var host = isColour ? (b.closest('.cc-swatch-container') || b) : b;
        var old = host.querySelector('.dspo-swatch-badge, .dspo-swatch-dot');
        if (old) old.remove();
        if (!s || (s.state !== 'preorder' && s.state !== 'lead_time')) return;
        var label = s.state === 'lead_time' ? 'Made to order' : 'Pre-order';
        var el = document.createElement('span');
        el.className = isColour ? 'dspo-swatch-dot' : 'dspo-swatch-badge';
        if (!isColour) el.textContent = label;
        el.setAttribute('aria-hidden', 'true');
        host.classList.add('dspo-swatch-host');
        host.appendChild(el);
        var t = b.getAttribute('title') || '';
        if (t.indexOf(label) === -1) b.setAttribute('title', (t ? t + ', ' : '') + label);
        var aria = b.getAttribute('aria-label');
        if (aria && aria.indexOf(label) === -1) b.setAttribute('aria-label', aria + ', ' + label);
      });
    });
  }

  function getLabel(node) { return node.tagName === 'INPUT' ? node.value : node.textContent.trim(); }
  function setLabel(node, text) { if (node.tagName === 'INPUT') node.value = text; else node.textContent = text; }

  /* ---------- main ---------- */

  function init(root) {
    if (root.__dspo) return;
    root.__dspo = true;

    var variants;
    try { variants = JSON.parse(root.querySelector('[data-dspo-variants]').textContent); } catch (e) { return; }

    Object.keys(variants).forEach(function (id) {
      var t = despatchText(variants[id]);
      if (t) KNOWN[id] = t;
    });

    var form = findForm(root);
    if (!form) return;
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return;

    var cutoff = parseInt(root.getAttribute('data-cutoff'), 10) || 16;
    var showInstock = root.getAttribute('data-show-instock') !== 'false';

    var instock = root.querySelector('[data-dspo-instock]');
    var reserve = root.querySelector('[data-dspo-reserve]');
    var rTitle = root.querySelector('[data-dspo-title]');
    var rSub = root.querySelector('[data-dspo-sub]');
    var rTicks = root.querySelector('[data-dspo-ticks]');
    var sooner = root.querySelector('[data-dspo-sooner]');
    var grid = root.querySelector('[data-dspo-grid]');
    var notifyVariant = root.querySelector('[data-dspo-notify-variant]');
    var notifyBody = root.querySelector('[data-dspo-notify-body]');
    var added = root.querySelector('[data-dspo-added]');
    var viewTracked = {};

    var btn = findButton(root, form);
    var labelNode = buttonLabelNode(btn);
    // The theme can render the button already in pre-order state, so take the default label from the attribute when present
    var originalLabel = (btn && btn.getAttribute('data-dspo-default-label')) || (labelNode ? getLabel(labelNode) : 'Add to basket');
    var wantedLabel = null;

    // Visible line item property, travels to basket, checkout, order and notification emails
    var prop = form.querySelector('input[name="' + PROP_NAME + '"]');
    if (!prop) {
      prop = document.createElement('input');
      prop.type = 'hidden';
      prop.name = PROP_NAME;
      prop.disabled = true;
      form.appendChild(prop);
    }

    var lastId = null;

    function apply(force) {
      var id = idInput.value;
      if (!force && id === lastId) { enforceLabel(); return; }
      lastId = id;
      var v = variants[id];
      var state = v ? (v.state || 'in_stock') : 'in_stock';
      if (state !== 'preorder' && state !== 'lead_time') state = 'in_stock';
      root.setAttribute('data-state', state);
      if (state !== 'in_stock' && v && !viewTracked[id]) {
        viewTracked[id] = true;
        track('dspo_preorder_view', { dspo_sku: v.sku, dspo_state: state, dspo_alternatives: (v.alternatives || []).length });
      }

      // In-stock line (only when this block owns it)
      if (instock) {
        instock.hidden = !(state === 'in_stock' && showInstock);
        var cd = instock.querySelector('[data-dspo-countdown]');
        if (cd && !instock.hidden) cd.innerHTML = countdownHtml(cutoff);
      }

      // Reserve card
      if (reserve) {
        if (state === 'in_stock') {
          reserve.hidden = true;
        } else {
          var lead = state === 'lead_time';
          var eta = etaFor(v);
          rTitle.textContent = lead ? 'Made to order' : 'Pre-order';
          if (eta.isDate) {
            rSub.innerHTML = 'Order now, despatch on or before <span class="dspo__date">' + esc(eta.text) + '</span>';
          } else if (lead) {
            rSub.textContent = eta.text ? 'Order now, ready to despatch in ' + eta.text : 'Order now, we\u2019ll confirm your despatch date by email';
          } else {
            rSub.textContent = eta.text ? 'Order now, next batch despatches ' + eta.text : 'Order now, despatched as soon as the next batch lands';
          }
          var ticks = [];
          if (v.perk) ticks.push('<li class="dspo__tick dspo__tick--perk"><strong>Pre-order bonus:</strong> <span>' + esc(v.perk) + '</span></li>');
          ticks.push('<li class="dspo__tick dspo__tick--price"><strong>Price locked</strong><span>at today\u2019s price</span></li>');
          ticks.push('<li class="dspo__tick dspo__tick--delivery"><strong>Free delivery</strong><span>UK mainland</span></li>');
          ticks.push(lead
            ? '<li class="dspo__tick dspo__tick--mail"><strong>Date confirmed</strong><span>by email</span></li>'
            : '<li class="dspo__tick dspo__tick--mail"><strong>Tracked</strong><span>link sent by email</span></li>');
          rTicks.innerHTML = ticks.join('');
          reserve.hidden = false;
        }
      }

      // Button label: short and positive; the date sits in the card directly beneath
      if (state === 'preorder') wantedLabel = 'Pre-order now';
      else if (state === 'lead_time') wantedLabel = 'Order now';
      else wantedLabel = null;
      if (btn) btn.classList.toggle('dspo-btn--preorder', state !== 'in_stock');
      enforceLabel();

      // Line item property
      if (state === 'preorder' || state === 'lead_time') {
        prop.value = despatchText(v);
        prop.disabled = false;
      }
      else { prop.value = ''; prop.disabled = true; }

      // Buy it now / Shop Pay: send pre-orders through the basket so the despatch note is seen
      var dyn = document.querySelectorAll('.shopify-payment-button');
      for (var i = 0; i < dyn.length; i++) {
        if (form.contains(dyn[i]) || (form.parentNode && form.parentNode.contains(dyn[i]))) dyn[i].style.display = state === 'in_stock' ? '' : 'none';
      }

      // Need it sooner
      if (state === 'in_stock' || !v) {
        sooner.hidden = true;
      } else {
        grid.innerHTML = '';
        if (added) added.hidden = true;
        (v.alternatives || []).forEach(function (a, idx) {
          var reason = a.note ? a.note.charAt(0).toUpperCase() + a.note.slice(1) : 'Ready to ship';
          var card = document.createElement('div');
          card.className = 'dspo__alt';
          card.innerHTML =
            '<a class="dspo__alt-link" href="' + esc(a.url) + '">' +
              (a.image ? '<img class="dspo__alt-img" src="' + esc(a.image) + '" alt="" loading="lazy" width="120" height="120">' : '') +
              '<span class="dspo__alt-body">' +
                '<span class="dspo__alt-reason">' + esc(reason) + '</span>' +
                '<span class="dspo__alt-title">' + esc(a.title) + '</span>' +
                '<span class="dspo__alt-price">' +
                  '<span class="dspo__p-inc">' + esc(a.price) + '</span>' +
                  '<span class="dspo__p-ex">' + esc(a.price_ex || a.price) + '<small> ex VAT</small></span>' +
                '</span>' +
              '</span>' +
            '</a>' +
            (a.variant_id ? '<button type="button" class="dspo__alt-add">Add</button>' : '');
          var link = card.querySelector('.dspo__alt-link');
          link.setAttribute('aria-label', reason + ': ' + a.title + ', ' + a.price + ', in stock');
          link.addEventListener('click', function () {
            track('dspo_alt_click', { dspo_from_sku: v.sku, dspo_to_title: a.title, dspo_position: idx + 1 });
          });
          var add = card.querySelector('.dspo__alt-add');
          if (add) {
            add.setAttribute('aria-label', 'Add ' + a.title + ' to basket');
            add.addEventListener('click', function () {
              if (add.disabled) return;
              add.disabled = true;
              add.textContent = 'Adding';
              fetch('/cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ items: [{ id: a.variant_id, quantity: 1 }] })
              }).then(function (r) {
                if (!r.ok) throw new Error('add failed');
                return r.json();
              }).then(function () {
                add.textContent = 'Added';
                add.classList.add('is-added');
                card.classList.add('is-added');
                if (added) {
                  added.innerHTML = '<strong>' + esc(reason) + '</strong> added to your basket. <button type="button" class="dspo__added-link">View basket</button>';
                  added.hidden = false;
                  added.querySelector('.dspo__added-link').addEventListener('click', openBasket);
                }
                refreshBasket();
                track('dspo_alt_add', { dspo_from_sku: v.sku, dspo_to_title: a.title, dspo_to_variant: a.variant_id, dspo_position: idx + 1, value: a.price });
                setTimeout(function () { add.disabled = false; add.textContent = 'Add again'; }, 2500);
              }).catch(function () {
                add.disabled = false;
                add.textContent = 'Add';
                if (added) { added.textContent = 'Sorry, that didn\u2019t add. Please try again or open the product.'; added.hidden = false; }
              });
            });
          }
          grid.appendChild(card);
        });
        var panel = root.querySelector('[data-dspo-panel]');
        if (panel) panel.hidden = !(v.alternatives && v.alternatives.length);
        sooner.hidden = false;
      }

      // Notify form
      if (v) {
        var label = v.title + (v.sku ? ' (' + v.sku + ')' : '');
        if (notifyVariant) notifyVariant.value = label;
        if (notifyBody) notifyBody.value = 'Back in stock request: ' + document.title.split('|')[0].trim() + ', ' + label;
      }
    }

    // The theme rewrites its button on variant change; re-apply our label afterwards.
    function enforceLabel() {
      if (!labelNode) return;
      if (!labelNode.isConnected) { btn = findButton(root, form); labelNode = buttonLabelNode(btn); if (!labelNode) return; }
      var current = getLabel(labelNode);
      if (wantedLabel && current !== wantedLabel && !/sold out|unavailable/i.test(current)) setLabel(labelNode, wantedLabel);
      if (!wantedLabel && current !== originalLabel && /^(pre-order|order now)/i.test(current)) setLabel(labelNode, originalLabel);
    }

    // Variant change detection: events the theme fires, form changes, plus a light poll as a safety net.
    form.addEventListener('change', function () { setTimeout(apply, 60); });
    document.addEventListener('change', function (e) {
      if (e.target && e.target.form === form) setTimeout(apply, 60);
    });
    ['variant:change', 'variant:changed', 'on:variant:change', 'product:variant-change'].forEach(function (evt) {
      document.addEventListener(evt, function () { setTimeout(apply, 60); });
    });
    var poll = setInterval(function () {
      if (!root.isConnected) { clearInterval(poll); clearInterval(tick); return; } // swapped out by the theme
      apply();
      root.setAttribute('data-vat', vatMode());
    }, 400);

    // Countdown refresh
    var tick = setInterval(function () {
      var cd = root.querySelector('[data-dspo-countdown]');
      if (cd && instock && !instock.hidden) cd.innerHTML = countdownHtml(cutoff);
    }, 30000);

    // Tracking: pre-order adds and notify sign-ups
    form.addEventListener('submit', function () {
      var st = root.getAttribute('data-state');
      if (st === 'preorder' || st === 'lead_time') {
        var cur = variants[idInput.value] || {};
        track('dspo_preorder_add', { dspo_sku: cur.sku, dspo_state: st, dspo_eta: prop.value });
      }
    }, true);
    var nf = root.querySelector('.dspo__notify');
    if (nf) nf.addEventListener('submit', function () {
      var cur = variants[idInput.value] || {};
      track('dspo_notify_submit', { dspo_sku: cur.sku });
    });

    root.setAttribute('data-vat', vatMode());
    document.addEventListener('change', function (e) {
      if (e.target && (e.target.id === 'toggle-vat-on' || e.target.id === 'toggle-vat-off')) {
        setTimeout(function () { root.setAttribute('data-vat', vatMode()); }, 50);
      }
    });

    apply(true);
  }

  function boot() {
    document.querySelectorAll('[data-dspo]').forEach(init);
    decorateSwatches();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);

  // Swatch switchers swap product HTML in without a page load, and swapped-in scripts never run.
  // Watch for a new pre-order block arriving and start it.
  if ('MutationObserver' in window) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () { pending = false; boot(); }, 50);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();